// front-end/multimodal/src/DictionaryWord.tsx
import React, { useContext, useState, useEffect } from 'react';
import CloseIcon from './CloseIcon';
import AudioPlayer from './AudioPlayer';
import './DictionaryWord.css';
import { MyContext } from "./Context";
import FontSelector from './FontSelector';
import type { PlayerConfig } from './types/story.ts'; // Import PlayerConfig
import { getStorageJSON } from './utils'; // Added import

// Interfaces
interface AudioConfig {
  speaking_rate: number;
  url: string;
}

interface WordExplanation {
  id?: string; // Optional, might be used elsewhere but not directly here
  has_dictionary: boolean;
  explanation: string;
  has_wiktionary: boolean;
}

interface DictionaryWordProps {
  baseUri: string;
  word: string;
  explanation: WordExplanation;
  onClose: () => void;
  audioConfigs: AudioConfig[];
}

const DictionaryWord: React.FC<DictionaryWordProps> = ({ 
  baseUri, 
  word, 
  explanation, 
  onClose, 
  audioConfigs 
}) => {
  const { isScriptFontState } = useContext(MyContext);

  // State type for the single player config used here
  interface DictionaryAudioPlayerState {
    languageCode: 'th'; // Assuming always Thai for dictionary word
    speakingRate: number;
  }

  const [dictionaryAudioPlayer, setDictionaryAudioPlayer] = useState<DictionaryAudioPlayerState>(() => {
    // Ensure audioConfigs is not empty before accessing
    const defaultRate = audioConfigs.length > 0 
      ? audioConfigs.slice().sort((a, b) => a.speaking_rate - b.speaking_rate)[0].speaking_rate
      : 1.0; // Default to 1.0 if no configs

    const defaultValue: DictionaryAudioPlayerState = {
      languageCode: 'th',
      speakingRate: defaultRate
    };

    // This call will now use the imported getStorageJSON
    const storedValue = getStorageJSON<DictionaryAudioPlayerState>('dictionaryAudioPlayer', defaultValue);
    const validRates = audioConfigs.map(cfg => cfg.speaking_rate);

    // Type check storedValue before accessing properties
    if (typeof storedValue !== 'object' || storedValue === null || typeof storedValue.speakingRate !== 'number') {
      return defaultValue; // Return default if stored value is invalid
    }

    if (!validRates.includes(storedValue.speakingRate)) {
      storedValue.speakingRate = defaultValue.speakingRate;
    }

    return storedValue as DictionaryAudioPlayerState; // Assert type after checks
  });

  useEffect(() => {
    localStorage.setItem('dictionaryAudioPlayer', JSON.stringify(dictionaryAudioPlayer));
  }, [dictionaryAudioPlayer]);

  // This handler expects the full players array, adapt if AudioPlayer passes differently
  const handleUpdatePlayers = (updatedPlayers: PlayerConfig[]) => {
    // This function is required by AudioPlayerProps, but in simplified mode with
    // onUpdateSpeakingRate provided, it might not be directly called for rate changes.
    // However, ensuring its correctness is good practice.
    if (updatedPlayers && updatedPlayers.length > 0) {
      const relevantPlayerConfig = updatedPlayers[0];
      // In simplified mode for DictionaryWord, we assume the first player is the Thai player.
      // We only care about its speakingRate.
      setDictionaryAudioPlayer(prev => ({ 
        ...prev, 
        speakingRate: relevantPlayerConfig.speakingRate 
      }));
    }
  };

  const handleUpdateSpeakingRate = (rate: number) => {
    setDictionaryAudioPlayer(prev => ({ ...prev, speakingRate: rate }));
  };

  return (
    <div className="dictionary-word-container">
      <h1 className={"title thai" + (isScriptFontState ? " thai-script-font" : "")}>
        <span>{word}</span>
      </h1>
      <p className="explanation">
        {explanation.has_dictionary 
          ? explanation.explanation 
          : "This word is not currently in our dictionary. It may be a name, a specialized term, or a new loan word."}
      </p>

      {explanation.has_wiktionary === false ? (
        <span className="wiki deactivated" title="Does not have a Wiktionary page">
          Wiktionary
        </span>
      ) : (
        <span className="wiki">
          <a href={`https://en.wiktionary.org/wiki/${encodeURIComponent(word)}`} target="_blank" rel="noreferrer">
            Wiktionary
          </a>
        </span>
      )}

      {/* Pass required props to AudioPlayer, even if unused in simplified mode */}
      <AudioPlayer
        baseUri={baseUri}
        audioConfigEnglish={[]}
        audioConfigThai={audioConfigs}
        players={[dictionaryAudioPlayer]}
        simplified={true}
        // --- Placeholder props to satisfy TS --- 
        sentenceIndex={0} // Placeholder
        isAutoStepForward={false} // Placeholder
        onAutoStepForwardToggle={() => {}} // Placeholder
        onRemovePlayer={() => {}} // Placeholder
        onAddPlayer={() => {}} // Placeholder
        onAudioComplete={() => {}} // Placeholder
        isThaiWordFocused={false} // Placeholder
        // --- Props needed for simplified mode --- 
        onUpdatePlayers={handleUpdatePlayers} 
        onUpdateSpeakingRate={handleUpdateSpeakingRate}
      />

      <CloseIcon onClose={onClose} />
      <FontSelector />
    </div>
  );
};

export default DictionaryWord;